/* iter-p4-reconcile-modal — Selective, previewable "Push Loom → Reader".
 * Two-step dialog:
 *   1. Admin picks which fields (title/price/isbn/author) to push.
 *   2. We run a dry_run reconcile, show the diff preview, admin confirms.
 *   3. Real reconcile fires with the selected only_fields.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'price_cents', label: 'Price',  fmt: (v) => (typeof v === 'number' ? `$${(v / 100).toFixed(2)}` : String(v)) },
  { key: 'title',       label: 'Title',  fmt: (v) => String(v ?? '') },
  { key: 'isbn',        label: 'ISBN',   fmt: (v) => String(v ?? '') },
  { key: 'author',      label: 'Author', fmt: (v) => String(v ?? '') },
];

export default function ReconcilePreviewDialog({ http, drifted, onClose, onDone }) {
  const [selected, setSelected] = useState(() =>
    new Set(FIELDS.map((f) => f.key)));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('pick');   // 'pick' | 'preview' | 'done'

  const toggle = (key) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runPreview = async () => {
    if (selected.size === 0) { toast.error('Pick at least one field to push.'); return; }
    setBusy(true);
    try {
      const r = await http.post('/admin/companion/sync-report/reconcile',
        { dry_run: true, only_fields: [...selected] });
      setPreview(r.data);
      setStage('preview');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
    } finally { setBusy(false); }
  };

  const confirmPush = async () => {
    setBusy(true);
    try {
      const r = await http.post('/admin/companion/sync-report/reconcile',
        { dry_run: false, only_fields: [...selected] });
      toast.success(`Reconciled — patched ${r.data.counts.patched}, skipped ${r.data.counts.skipped}, failed ${r.data.counts.failed}.`);
      setStage('done');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="reconcile-preview-dialog">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[13px] font-semibold">
            {stage === 'pick'    && 'Push Loom → Reader · pick fields'}
            {stage === 'preview' && 'Push Loom → Reader · preview'}
            {stage === 'done'    && 'Push complete'}
          </div>
          <button type="button" onClick={onClose} className="rounded-sm p-1 hover:bg-secondary"
                  data-testid="reconcile-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {stage === 'pick' && (
          <>
            <p className="mb-2 text-[11.5px] text-muted-foreground">
              Choose which fields to overwrite on the Reader catalog. Any
              drifted field NOT ticked stays as-is on the Reader side.
            </p>
            <div className="mb-3 grid grid-cols-2 gap-2">
              {FIELDS.map((f) => (
                <label key={f.key}
                       data-testid={`reconcile-field-${f.key}`}
                       className={`flex cursor-pointer items-center gap-2 rounded-sm border p-2 text-[12px] ${selected.has(f.key) ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <input type="checkbox" checked={selected.has(f.key)}
                         onChange={() => toggle(f.key)} />
                  {f.label}
                </label>
              ))}
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">
              {drifted?.length ?? 0} drifted book(s) will be reviewed.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose}
                      className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary">
                Cancel
              </button>
              <button type="button" onClick={runPreview} disabled={busy || selected.size === 0}
                      data-testid="reconcile-preview-btn"
                      className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Preview changes
              </button>
            </div>
          </>
        )}

        {stage === 'preview' && preview && (
          <>
            <div className="mb-2 text-[11.5px] text-muted-foreground">
              Ready to patch {preview.counts.patched} book(s). Skipped: {preview.counts.skipped}. Fields: <b>{[...selected].join(', ')}</b>.
            </div>
            <div className="mb-3 max-h-72 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="border-b border-border text-left text-[10px] uppercase text-muted-foreground">
                    <th className="p-2">Book</th><th className="p-2">Reader ID</th><th className="p-2">Patch</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.patched.map((p, i) => (
                    <tr key={i} className="border-b border-border/60" data-testid={`reconcile-preview-row-${i}`}>
                      <td className="p-2 font-semibold">{p.title}</td>
                      <td className="p-2 font-mono text-[10.5px] text-muted-foreground">{p.reader_id}</td>
                      <td className="p-2">
                        <ul className="space-y-0.5">
                          {Object.entries(p.would_patch || {}).map(([k, v]) => {
                            const f = FIELDS.find((x) => x.key === k);
                            return (
                              <li key={k} className="font-mono text-[10.5px]">
                                {f?.label || k}: <b className="text-emerald-700 dark:text-emerald-300">{f ? f.fmt(v) : String(v)}</b>
                              </li>
                            );
                          })}
                        </ul>
                      </td>
                    </tr>
                  ))}
                  {preview.skipped.length > 0 && preview.skipped.map((s, i) => (
                    <tr key={`s-${i}`} className="border-b border-border/60 opacity-60">
                      <td className="p-2 italic">{s.title}</td><td colSpan={2} className="p-2 text-muted-foreground">skipped: {s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setStage('pick')}
                      className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary">
                Back
              </button>
              <button type="button" onClick={confirmPush} disabled={busy || preview.counts.patched === 0}
                      data-testid="reconcile-confirm-btn"
                      className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Confirm push
              </button>
            </div>
          </>
        )}

        {stage === 'done' && (
          <div className="flex flex-col items-center gap-2 p-4 text-[12px]">
            <Check className="h-8 w-8 text-emerald-500" />
            <div>Push complete. Sync report refreshing…</div>
            <button type="button" onClick={onClose}
                    className="mt-2 rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
